// ============ IN-HOUSE ERROR LOGGER ============
// Captures JS errors + unhandled promise rejections + React error boundary
// crashes and POSTs them to /api/errors. No third-party services.
// Fire-and-forget: never blocks UI, never throws itself.

import React from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENV = process.env.NODE_ENV === 'production' ? 'production' : 'preview';
const APP_VERSION = '1.0.0';

// Simple hash — takes user_id if logged in, produces a stable 8-char hex tag.
// This lets Marc SEE that crashes cluster on the same user without ever storing
// the user's real ID or any PII in the error log.
const hashUserId = (id) => {
  if (!id) return null;
  let h = 0;
  for (let i = 0; i < id.length; i++) h = ((h << 5) - h + id.charCodeAt(i)) | 0;
  return `u_${Math.abs(h).toString(16).slice(0, 8)}`;
};

const getCurrentUserIdHash = () => {
  try {
    const raw = localStorage.getItem('club_user');
    if (raw) return hashUserId(JSON.parse(raw).id);
  } catch { /* noop */ }
  return null;
};

const reported = new Set(); // dedupe within a session
const post = (payload) => {
  const sig = `${payload.type}|${(payload.message || '').slice(0, 200)}|${(payload.stack || '').slice(0, 200)}`;
  if (reported.has(sig)) return;
  reported.add(sig);

  const body = {
    ...payload,
    url: (window.location && window.location.href) || '',
    route: (window.location && window.location.pathname) || '',
    app_version: APP_VERSION,
    environment: ENV,
    user_id_hash: getCurrentUserIdHash(),
  };
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      navigator.sendBeacon(`${API}/errors`, blob);
    } else {
      fetch(`${API}/errors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => { /* silent */ });
    }
  } catch { /* absolutely silent */ }
};

// Global handlers — safe to call multiple times; guards prevent double-binding.
let _installed = false;
export const installGlobalErrorHandlers = () => {
  if (_installed) return;
  _installed = true;
  window.addEventListener('error', (e) => {
    post({
      type: 'error',
      message: e?.message || 'Unknown error',
      stack: (e?.error && e.error.stack) || '',
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e?.reason;
    post({
      type: 'unhandledrejection',
      message: (r && (r.message || String(r))) || 'Unhandled promise rejection',
      stack: (r && r.stack) || '',
    });
  });
};

// React error boundary — wraps <App /> in index.js to catch render-time crashes.
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false };
  }
  static getDerivedStateFromError() { return { crashed: true }; }
  componentDidCatch(error, info) {
    post({
      type: 'boundary',
      message: (error && error.message) || 'React render error',
      stack: (error && error.stack) || (info && info.componentStack) || '',
    });
  }
  render() {
    if (this.state.crashed) {
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', padding: 24,
          background: 'var(--clubhouse-cream, #f5f0e8)', color: '#0F3B2E',
          textAlign: 'center', fontFamily: '-apple-system, sans-serif',
        }} data-testid="error-boundary-fallback">
          <h1 style={{ fontSize: 22, marginBottom: 8 }}>Something went wrong</h1>
          <p style={{ maxWidth: 320, fontSize: 14, opacity: 0.75, marginBottom: 20 }}>
            The Club Golf ran into an unexpected error. We&apos;ve logged it and will fix it.
            Try reloading the app.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px', borderRadius: 999, background: '#0F3B2E',
              color: '#f5f0e8', border: 'none', fontWeight: 600, cursor: 'pointer',
            }}
            data-testid="error-boundary-reload-btn"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
